import { AllEntities } from "components/entities/utils";
import { CreateLeadFormOutput } from "components/leads/create_lead/utils";
import { AddLeadEntitiesInput_Entities } from "lib/generated/app-service-gql/graphql";
import { LeadEntityType } from "lib/types/graphQLEnums";

/** Entity types that can be bulk-saved to a lead (i.e. a lead can have many of them). */
export type BulkSaveEntityType = "contact" | "contact_cognism";

/**
 * A single bulk-save target.
 * - `entityId`: the save/attach handle (Cognism: the stable contact id; Oscar: oscar_contacts.id).
 * - `savedEntityId`: the stored lead `entity_id` for the "already saved to lead" lookup. Equals
 *   `entityId` for Oscar; for Cognism it's the `cognism_redeemed_contact` row id, present only once
 *   the contact has been redeemed (else `undefined` → excluded from the lookup, i.e. not-yet-saved).
 */
export type BulkSaveEntity = {
  entityType: BulkSaveEntityType;
  entityId: string;
  savedEntityId?: string;
};

type FormEntities = NonNullable<CreateLeadFormOutput["entities"]>;

export type BulkSaveEntityConfig = {
  /** Singular UI label, used in tooltip copy and tracking. */
  label: string;
  /**
   * Entities to prefill on the create-lead form.
   */
  idsForForm: (entityIds: string[]) => FormEntities;
};

export const BULK_SAVE_ENTITY_CONFIG: Record<BulkSaveEntityType, BulkSaveEntityConfig> = {
  contact: {
    label: "contact",
    idsForForm: (entityIds) =>
      entityIds.map((entityId) => ({ entityId, entityType: LeadEntityType.Contact })),
  },
  contact_cognism: {
    label: "contact",
    // Cognism contacts are redeemed on save (the server derives their entity ids),
    // so nothing is prefilled — they're attached after the lead is created.
    idsForForm: () => [],
  },
};

/**
 * Entities that must be attached *after* a new lead is created rather than created with it.
 * Cognism contacts are redeemed on save, so they're attached via `addLeadEntities` carrying their
 * stable contact id (the server resolves the redeem handle); every other entity type is created
 * together with the lead, so this returns `null`.
 */
export function entitiesToAttachAfterCreate(
  entityType: AllEntities,
  entityIds: string[],
): AddLeadEntitiesInput_Entities[] | null {
  if (entityType !== "contact_cognism") return null;
  return entityIds.map((cognismContactId) => ({
    entityType: LeadEntityType.ContactCognism,
    cognismContactId,
  }));
}

// --- Mixed-selection helpers (unified bulk save across sources) --------------
// These fold a heterogeneous `contact` + `contact_cognism` selection through the
// same per-type config, so one bulk save button can handle both sources at once.

/**
 * Ids for the "which leads is this already saved to" lookup, across a mixed selection.
 * Uses each entity's `savedEntityId` (the stored lead `entity_id`): the native id for Oscar,
 * the redeemed-contact row id for Cognism. Entities without one (e.g. a not-yet-redeemed
 * Cognism contact) can't be on a lead, so they're excluded.
 */
export function bulkSaveLookupIds(entities: BulkSaveEntity[]): string[] {
  return entities.flatMap((e) => (e.savedEntityId ? [e.savedEntityId] : []));
}

/** Entities to prefill on the create-lead form, across a mixed selection. */
export function bulkSaveFormEntities(entities: BulkSaveEntity[]): FormEntities {
  return entities.flatMap((e) => BULK_SAVE_ENTITY_CONFIG[e.entityType].idsForForm([e.entityId]));
}

/**
 * Entities that must be attached after the lead is created (Cognism contacts,
 * redeemed-on-save), across a mixed selection. Null when there are none.
 */
export function bulkSaveEntitiesToAttach(
  entities: BulkSaveEntity[],
): AddLeadEntitiesInput_Entities[] | null {
  const attach = entities.flatMap(
    (e) => entitiesToAttachAfterCreate(e.entityType, [e.entityId]) ?? [],
  );
  return attach.length > 0 ? attach : null;
}
